/**
 * Practice-area roadmap — single source of truth for the signup wizard's
 * "what's available, what's coming soon, and when" message.
 *
 * Update DATES below as verticals ship. The component picks the right
 * label/badge/stopwatch automatically based on the status field.
 *
 * Status legend:
 *   'available'   — on sale: offered at signup, in Settings and on the
 *                   landing fee schedule
 *   'granted'     — built and working, but NOT on sale yet. Never offered in
 *                   any picker; an account gets it only when the backend sets
 *                   it on the user's practice_areas (how the team tests it on
 *                   dev). Existing grants keep working everywhere else.
 *   'ticking'     — exactly ONE vertical may use this; renders a live
 *                   stopwatch counting down to `eta`. By design, only the
 *                   *next* vertical to ship gets the ticking treatment so
 *                   we don't have 7 timers on screen.
 *   'dated'       — has a concrete eta but no live tick (static "Coming ~Aug 2026")
 *   'coming_soon' — no eta yet; shows a generic "Coming soon" badge
 */
import type { PracticeArea } from '../../types/practiceArea';

export type PracticeAreaStatus = 'available' | 'granted' | 'ticking' | 'dated' | 'coming_soon';

export interface PracticeAreaRoadmapEntry {
  value: PracticeArea;
  label: string;
  status: PracticeAreaStatus;
  /** ISO-8601 timestamp; only meaningful when status is 'ticking' or 'dated'. */
  eta?: string;
  /** One-line tagline shown when expanded. */
  tagline?: string;
}

// Updated 2026-10-19: the paid launch is CRIMINAL ONLY. Consumer Disputes
// (civil) and Corporate are built but not complete, so they are 'granted' —
// absent from signup, Settings and pricing, reachable only by a backend grant
// (team testing on dev). Flip one back to 'available' the day it goes on sale,
// together with its row on the landing fee schedule (PricingBand.tsx).
// Remaining verticals have no feature set yet and stay 'coming_soon' with NO
// date (we don't promise ETAs we can't hold). A lawyer must never be able to
// pick a vertical that has no working product.
//
// Display order is intentional — it's the order users will see on screen.
export const PRACTICE_AREA_ROADMAP: PracticeAreaRoadmapEntry[] = [
  {
    value: 'criminal',
    label: 'Criminal',
    status: 'available',
    tagline: 'BNS, BNSS, BSA · FIR analysis · bail · case theory',
  },
  {
    value: 'civil_consumer',
    label: 'Consumer Disputes',
    status: 'granted',
    tagline: 'CPA 2019 · notice analysis · forum & limitation',
  },
  {
    value: 'corporate',
    label: 'Corporate & Commercial',
    status: 'granted',
    tagline: 'Contracts · M&A · compliance calendars · DD workflows',
  },
  {
    value: 'family',
    label: 'Family & Matrimonial',
    status: 'coming_soon',
    tagline: 'Divorce · custody · maintenance · domestic violence',
  },
  {
    value: 'civil_property',
    label: 'Property & Real Estate',
    status: 'coming_soon',
    tagline: 'Title · partition · RERA · landlord–tenant',
  },
  {
    value: 'tax',
    label: 'Tax',
    status: 'coming_soon',
    tagline: 'GST · income tax · tribunal precedent',
  },
  {
    value: 'labor',
    label: 'Labor & Employment',
    status: 'coming_soon',
    tagline: 'POSH · industrial disputes · IR Code',
  },
  {
    value: 'other',
    label: 'Other',
    status: 'coming_soon',
    tagline: "Tell us what you practise — we'll prioritise next",
  },
];

/** The single 'ticking' vertical, if any. Used by the stopwatch component
 * to know which one to animate. */
export const TICKING_VERTICAL: PracticeAreaRoadmapEntry | undefined =
  PRACTICE_AREA_ROADMAP.find((e) => e.status === 'ticking');

/** Default selection when the wizard opens. Criminal leads the list and is a
 *  safe default (on sale). */
export const DEFAULT_PRACTICE_AREA: PracticeArea = 'criminal';
